import { ConsentWorkflow, FormDefinitionType, IEConsentForm, PatientStateType } from '@/api/dtos';
import { AppSettings } from '@/AppSettings';
import { crfReadOnly } from '@/utils/CrfFormUtils';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import {
    CollaboratingGroupContext,
    CrfForm,
    FormBreadcrumbs,
    FormContext,
    FormDefinitionContext,
    FormErrorDisplayMode,
    IFormContext,
    InstitutionContext,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RouteLoading,
} from '@ngt/opms';
import { ValidateOn } from '@ngt/forms-core';
import { FC, PropsWithChildren, useContext, useMemo } from 'react';
import ConsentStatusActions from './ConsentStatusActions';
import { Box } from '@mui/material';

export interface ConsentStandardFormProps extends PropsWithChildren {
    formDefinitionId: FormDefinitionType;
}

const ConsentStandardForm: FC<ConsentStandardFormProps> = ({
    formDefinitionId,
    children,
}) => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);
    const { data: formDefinition } = useContext(FormDefinitionContext);
    const { data: form } = useContext(FormContext) as unknown as IFormContext<IEConsentForm>;

    const {
        permissions: [canUpdateCrf],
        loading: permissionsLoading,
    } = usePermissionsByIds(
        [Permission.OpmsCrfUpdate],
        masterGroup?.id,
        collaboratingGroup?.id,
        institution?.id,
        patient?.id
    );

    const hasChanged = useMemo(
        () => form?.enteredDate !== form?.modifiedDate,
        [form]
    );
    const isEConsent = form?.workflow === ConsentWorkflow.EConsent;
    const formDisabled = !canUpdateCrf || isEConsent;
    const showConsentStatusAction = patient?.patientStateId != PatientStateType.ScreeningFailed &&
        patient?.patientStateId != PatientStateType.ScreeningFailed;

    if (permissionsLoading) {
        return <RouteLoading />;
    }

    return (
        <PageLayout breadcrumbs={<FormBreadcrumbs />}>
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(!formDisabled)}
                debug={AppSettings.debug}
                errorMode={
                    isEConsent
                        ? FormErrorDisplayMode.AlwaysHideErrors
                        : hasChanged
                            ? FormErrorDisplayMode.AlwaysShowAllErrors
                            : FormErrorDisplayMode.AutoShowAllErrors
                }
            >
                {
                    showConsentStatusAction && <ConsentStatusActions
                        formDefinitionId={formDefinitionId}
                    />
                }
                {
                    formDefinition?.instructions && <Box
                        sx={{
                            p: 2,
                        }}
                    >
                        {formDefinition?.instructions}
                    </Box>
                }
                {children}
            </CrfForm>
        </PageLayout>
    );
};

export default ConsentStandardForm;
